import { AsyncPipe } from '@angular/common';
import type { TrackByFunction } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Output,
  forwardRef,
  input,
  signal,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import type { Observable } from 'rxjs';
import { combineLatest, map } from 'rxjs';
import { ControlValueAccessorDirective } from '../../directives/control-value-accessor.directive';
import { MatRippleModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatTooltip } from '@angular/material/tooltip';

interface ChipState {
  label: string;
  value: string | boolean;
  selected: boolean;
  disabled?: boolean;
  tooltip?: string;
}

@Component({
  selector: 'ui-single-select-buttons-toggle',
  standalone: true,
  imports: [AsyncPipe, MatRippleModule, MatButtonModule, MatTooltip],
  templateUrl: './single-select-buttons-toggle.component.html',
  styleUrls: ['./single-select-buttons-toggle.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SingleSelectButtonsToggleComponent),
      multi: true,
    },
  ],
})
export class SingleSelectButtonsToggleComponent extends ControlValueAccessorDirective<string> {
  trackByValue: TrackByFunction<ChipState> = (_, c) => c.value;

  value = signal<unknown>('');
  options = input<ButtonsToggleSingleSelectOption[]>([]);
  state$: Observable<ChipState[]>;

  readonly cols = input(2);
  readonly invalid = input<boolean>(false);

  @Output() itemClicked = new EventEmitter<any>();

  constructor() {
    super();

    this.state$ = combineLatest([
      toObservable(this.options),
      toObservable(this.value),
      this.disabled$,
    ]).pipe(
      map(([options, value, disabled]) => {
        return options.map(o => ({
          ...o,
          disabled: disabled || o.disabled,
          selected: Boolean(value === o.value),
          tooltip: o.tooltip,
        }));
      })
    );
  }

  override writeValue(value: string): void {
    super.writeValue(value);
    this.value.set(value);
  }

  toggle(value: string | boolean, event?: MouseEvent): void {
    (event?.currentTarget as HTMLElement | undefined)?.focus();
    this.value.set(value);
    this.onTouched();
    this.onChange(this.value());
    this.itemClicked.emit(value);
  }
}

export interface ButtonsToggleSingleSelectOption {
  value: string | boolean;
  label: string;
  disabled?: boolean;
  tooltip?: string;
}
